import { gql, useQuery } from '@apollo/client';

import type {
  GetSkillEpisodesQuery,
  GetSkillEpisodesQueryVariables,
} from '@sbrc/gql/apollo-codegen';

const GET_SKILL_EPISODES = gql`
  query GetSkillEpisodes($taskId: String!, $pagination: PaginationRequest) {
    getTrainingDataSessionsByTaskId(taskId: $taskId, pagination: $pagination) {
      id
      name
      date
      created
      status
      reviews {
        id
        created
        status
      }
      thumbnailUrl
    }
  }
`;

// Mirrors the resolver's pagination contract — it dereferences `pagination.offset`
// unconditionally, so we must pass a concrete object rather than letting it be null.
//
// TODO: replace this hardcoded limit with real pagination. 200 is fine for
// positioning skills, where episode counts stay small, but control / other
// data-collection skills routinely exceed that. Plan to add a load-more or
// cursor-based fetch in a follow-up PR.
const DEFAULT_PAGINATION = { offset: 0, limit: 200 };

export type SkillEpisode = NonNullable<
  GetSkillEpisodesQuery['getTrainingDataSessionsByTaskId']
>[number];

export type EpisodeApprovalStatus = 'pass' | 'fail' | 'unreviewed';

/**
 * Compute an episode's overall approval status. Mirrors marvin-app's
 * convention (`SidebarItem.tsx`): no reviews ⇒ unreviewed; otherwise the
 * most recent review's status decides — `excluded` ⇒ fail, anything else ⇒
 * pass. Used by both the Episodes-tab summary header and per-row badges.
 */
export function getEpisodeApprovalStatus(
  episode: SkillEpisode,
): EpisodeApprovalStatus {
  if (!episode.reviews.length) return 'unreviewed';

  const parseTimestamp = (value: unknown): number => {
    const n = Number(value);

    return Number.isNaN(n) ? new Date(String(value)).getTime() : n;
  };

  const latest = episode.reviews.reduce((a, b) =>
    parseTimestamp(b.created) > parseTimestamp(a.created) ? b : a,
  );

  return latest.status === 'excluded' ? 'fail' : 'pass';
}

/**
 * Returns the presigned URL of the first-frame PNG uploaded by echo-bot at
 * recording-stop time, or null if the session has no such file. The URL is
 * resolved server-side by the `getTrainingDataSessionsByTaskId` resolver,
 * which batch-fetches thumbnail keys for all returned sessions in one query.
 */
export function getEpisodeThumbnailUrl(episode: SkillEpisode): string | null {
  return episode.thumbnailUrl ?? null;
}

export function useSkillEpisodes(taskId: string | null) {
  const { data, loading, error, refetch } = useQuery<
    GetSkillEpisodesQuery,
    GetSkillEpisodesQueryVariables
  >(GET_SKILL_EPISODES, {
    variables: { taskId: taskId ?? '', pagination: DEFAULT_PAGINATION },
    skip: !taskId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    episodes: data?.getTrainingDataSessionsByTaskId ?? [],
    loading,
    error,
    refetch,
  };
}
